<template>
  <section :aria-labelledby="headingId" data-cy="guidedRecording-takeReview">
    <div class="d-flex align-center ga-2 mb-2">
      <v-icon color="primary" aria-hidden="true">mdi-play-circle-outline</v-icon>
      <h2 :id="headingId" class="text-h6">{{ takeReviewHeading }}</h2>
    </div>
    <p class="text-body-2 mb-4">{{ $t('routes.guidedRecording.takeReviewDescription') }}</p>

    <div
      v-if="hasMeasuredDuration"
      class="guided-take-review__duration-status mb-4"
      role="status"
      aria-live="polite"
      aria-atomic="true"
      data-cy="guidedRecording-takeReview-duration"
    >
      <span>{{ measuredDurationMessage }}</span>
      <span
        v-if="take.automaticStop"
        data-cy="guidedRecording-takeReview-automaticStop"
      >
        {{ $t('routes.guidedRecording.recordingAutomaticStop') }}
      </span>
    </div>

    <v-alert
      v-if="!isTakeValid"
      ref="invalidTakeFeedback"
      type="error"
      variant="tonal"
      class="guided-take-review__invalid-take mb-4"
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      tabindex="-1"
      data-cy="guidedRecording-takeReview-invalidTake"
    >
      <div class="guided-take-review__invalid-take-content">
        <v-icon aria-hidden="true">mdi-alert-circle-outline</v-icon>
        <span>{{ invalidTakeMessage }}</span>
      </div>
    </v-alert>

    <v-alert
      v-if="playbackError"
      type="error"
      variant="tonal"
      role="alert"
      class="mb-4"
      data-cy="guidedRecording-takeReview-playbackError"
    >
      {{ $t('routes.guidedRecording.playbackError') }}
    </v-alert>

    <video
      v-if="isTakeValid"
      ref="reviewVideo"
      class="guided-take-review__video mb-4"
      :src="take.url"
      controls
      playsinline
      :aria-label="recordedTakeLabel"
      data-cy="guidedRecording-takeReview-video"
      @canplay="playbackError = false"
      @error="playbackError = true"
    />

    <v-alert
      v-if="uploadError"
      type="error"
      variant="tonal"
      role="alert"
      class="mb-4"
      data-cy="guidedRecording-takeReview-uploadError"
    >
      {{ uploadError }}
    </v-alert>

    <section v-if="isUploading" class="mb-4" :aria-label="$t('routes.guidedRecording.submissionProgress')">
      <p class="text-body-2 mb-2" aria-live="polite">
        {{ $t('routes.guidedRecording.submissionProgressValue', { progress }) }}
      </p>
      <v-progress-linear
        :model-value="progress"
        color="primary"
        height="10"
        rounded
        role="progressbar"
        :aria-label="$t('routes.guidedRecording.submissionProgress')"
        aria-valuemin="0"
        aria-valuemax="100"
        :aria-valuenow="progress"
        class="mb-4"
        data-cy="guidedRecording-takeReview-uploadProgress"
      />
      <v-btn
        color="error"
        variant="outlined"
        :loading="isCancelling"
        :disabled="isCancelling"
        data-cy="guidedRecording-takeReview-cancelUpload"
        @click="$emit('cancel-upload')"
      >
        <v-icon start aria-hidden="true">mdi-cancel</v-icon>
        {{ $t('routes.guidedRecording.cancelSubmission') }}
      </v-btn>
    </section>

    <div v-else-if="isTakeValid" class="guided-take-review__actions">
      <v-btn
        v-if="canDiscard"
        color="error"
        variant="outlined"
        data-cy="guidedRecording-takeReview-discard"
        @click="discard"
      >
        <v-icon start aria-hidden="true">mdi-delete-outline</v-icon>
        {{ $t('routes.guidedRecording.discardTake') }}
      </v-btn>
      <v-btn
        v-if="canDiscard"
        color="primary"
        data-cy="guidedRecording-takeReview-accept"
        @click="accept"
      >
        <v-icon start aria-hidden="true">mdi-check-circle-outline</v-icon>
        {{ $t('routes.guidedRecording.acceptTake') }}
      </v-btn>
      <v-btn
        v-else
        color="error"
        variant="outlined"
        :loading="isCancelling"
        :disabled="isCancelling"
        data-cy="guidedRecording-takeReview-cancelFailedUpload"
        @click="$emit('cancel-upload')"
      >
        <v-icon start aria-hidden="true">mdi-cancel</v-icon>
        {{ $t('routes.guidedRecording.cancelSubmission') }}
      </v-btn>
      <v-btn
        v-if="(canRetry || !canDiscard) && uploadError"
        color="primary"
        :disabled="isCancelling"
        data-cy="guidedRecording-takeReview-retryUpload"
        @click="$emit('retry-upload')"
      >
        <v-icon start aria-hidden="true">mdi-refresh</v-icon>
        {{ $t('routes.guidedRecording.recovery.retryUpload') }}
      </v-btn>
    </div>
    <div v-else-if="canDiscard" class="guided-take-review__actions">
      <v-btn
        color="error"
        variant="outlined"
        data-cy="guidedRecording-takeReview-discardInvalidTake"
        @click="discard"
      >
        <v-icon start aria-hidden="true">mdi-delete-outline</v-icon>
        {{ $t('routes.guidedRecording.discardTake') }}
      </v-btn>
    </div>
  </section>
</template>

<script>
const RECORDER_MIME_TYPES = [
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
];

const GUIDED_RECORDING_MINIMUM_DURATION_SECONDS = 5;
const GUIDED_RECORDING_MAXIMUM_DURATION_SECONDS = 600;
const GUIDED_RECORDING_AUTOMATIC_STOP_TOLERANCE_SECONDS = 1;

const isValidEffectiveDuration = (durationSeconds) => (
  Number.isInteger(durationSeconds)
  && durationSeconds >= GUIDED_RECORDING_MINIMUM_DURATION_SECONDS
  && durationSeconds <= GUIDED_RECORDING_MAXIMUM_DURATION_SECONDS
);

const isValidMeasuredDuration = (take, maximumDurationSeconds) => {
  if (!isValidEffectiveDuration(maximumDurationSeconds)
    || take?.durationValid !== true
    || typeof take?.automaticStop !== 'boolean'
    || take.invalidReason
    || typeof take?.measuredDurationSeconds !== 'number'
    || !Number.isFinite(take.measuredDurationSeconds)
    || take.measuredDurationSeconds < 0
  ) {
    return false;
  }

  const tolerance = take.automaticStop ? GUIDED_RECORDING_AUTOMATIC_STOP_TOLERANCE_SECONDS : 0;
  return take.measuredDurationSeconds <= maximumDurationSeconds + tolerance;
};

export default {
  name: 'GuidedTakeReview',
  props: {
    canDiscard: { type: Boolean, default: true },
    canRetry: { type: Boolean, default: false },
    isCancelling: { type: Boolean, default: false },
    isUploading: { type: Boolean, default: false },
    maximumDurationSeconds: {
      type: Number,
      default: undefined,
      validator: value => value === undefined || isValidEffectiveDuration(value),
    },
    progress: { type: Number, default: 0 },
    questionNumber: { type: Number, default: 0 },
    questionTotal: { type: Number, default: 0 },
    take: { type: Object, required: true },
    uploadError: { type: String, default: '' },
  },
  emits: ['accept', 'cancel-upload', 'discard', 'retry-upload'],
  data() {
    return {
      headingId: 'guided-recording-take-review-heading',
      playbackError: false,
    };
  },
  watch: {
    take: {
      handler() {
        this.playbackError = false;
        if (!this.isTakeValid) {
          this.focusInvalidTake();
        }
      },
      immediate: true,
    },
  },
  computed: {
    hasMeasuredDuration() {
      return typeof this.take?.measuredDurationSeconds === 'number'
        && Number.isFinite(this.take.measuredDurationSeconds)
        && this.take.measuredDurationSeconds >= 0;
    },
    isTakeValid() {
      const blob = this.take?.blob;
      const hasBlob = typeof Blob !== 'undefined' && blob instanceof Blob && blob.size > 0;

      return hasBlob
        && typeof this.take?.url === 'string'
        && Boolean(this.take.url)
        && RECORDER_MIME_TYPES.includes(this.take.mimeType)
        && isValidMeasuredDuration(this.take, this.maximumDurationSeconds);
    },
    invalidTakeMessage() {
      if (!isValidMeasuredDuration(this.take, this.maximumDurationSeconds)) {
        return this.$t('routes.guidedRecording.invalidTakeDuration');
      }

      return this.$t('routes.guidedRecording.emptyCapture');
    },
    measuredDurationMessage() {
      return this.$t('routes.guidedRecording.recordingMeasuredDuration', {
        duration: this.formatDuration(this.take?.measuredDurationSeconds),
      });
    },
    recordedTakeLabel() {
      if (!this.questionNumber || !this.questionTotal) {
        return this.$t('routes.guidedRecording.recordedTake');
      }

      return this.$t('routes.guidedRecording.recordedTakeForQuestion', {
        current: this.questionNumber,
        total: this.questionTotal,
      });
    },
    takeReviewHeading() {
      if (!this.questionNumber || !this.questionTotal) {
        return this.$t('routes.guidedRecording.takeReviewHeading');
      }

      return this.$t('routes.guidedRecording.takeReviewHeadingForQuestion', {
        current: this.questionNumber,
        total: this.questionTotal,
      });
    },
  },
  methods: {
    formatDuration(durationSeconds) {
      const safeDuration = Number.isFinite(durationSeconds) && durationSeconds >= 0
        ? durationSeconds
        : 0;
      const totalSeconds = Math.floor(safeDuration);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = String(totalSeconds % 60).padStart(2, '0');

      return `${minutes}:${seconds}`;
    },
    accept() {
      if (!this.isTakeValid || !this.canDiscard || this.isUploading) {
        this.focusInvalidTake();
        return;
      }
      this.$refs.reviewVideo?.pause();
      this.$emit('accept', this.take);
    },
    discard() {
      this.$refs.reviewVideo?.pause();
      this.$emit('discard');
    },
    focusInvalidTake() {
      this.$nextTick(() => {
        const feedback = this.$refs.invalidTakeFeedback;
        const element = feedback?.$el || feedback;
        element?.focus?.();
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@layer app {
  .guided-take-review__video {
    background: #090b0e;
    border-radius: 10px;
    display: block;
    max-height: 520px;
    width: 100%;
  }

  .guided-take-review__invalid-take:focus {
    outline: 2px solid rgb(var(--v-theme-primary));
    outline-offset: 4px;
  }

  .guided-take-review__invalid-take-content {
    align-items: flex-start;
    display: flex;
    gap: 8px;
  }

  .guided-take-review__duration-status {
    align-items: flex-start;
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
  }

  .guided-take-review__actions {
    align-items: center;
    display: flex;
    flex-wrap: nowrap;
    gap: 12px;
    justify-content: flex-end;
  }

  .guided-take-review__actions > .v-btn {
    flex: 0 0 auto;
    white-space: nowrap;
  }

  @media (max-width: 600px) {
    .guided-take-review__duration-status {
      flex-direction: column;
      gap: 4px;
    }

    .guided-take-review__actions {
      flex-direction: column-reverse;
    }
  }
}
</style>
